import { useStore } from '../store/useStore';
import { t } from '../i18n';
import { claimLocalDocuments, setCurrentOwnerId, setLibraryWriteListener } from './library';
import { clearSyncState, dexieLocalSync } from './syncLocal';
import { forgetVaultKey, vaultStatus } from './crypto/vault';
import { firestoreRemoteSync } from './syncRemote';
import { createSyncEngine, type SyncEngine } from './syncEngine';

/**
 * Sync engine (phase B). When a user signs in:
 *   1. claim previously-anonymous local docs (assign ownerId = uid),
 *   2. subscribe to their Firestore `documents` and reconcile each change
 *      against what this device last agreed on with the cloud,
 *   3. mirror every subsequent local write up, coalesced and batched.
 *
 * The rules live in syncDecide.ts and the loop in syncEngine.ts, both without
 * Firebase in them, so two devices can be played against each other in tests.
 * This file is the wiring: Firestore, Dexie, and the status the UI shows.
 *
 * Firestore's client offline persistence queues writes made while offline, so
 * edits sync when the connection returns. Local-only usage (signed out) is
 * unchanged — none of this runs.
 */
let currentUid: string | null = null;
let engine: SyncEngine | null = null;

const onOffline = (): void => {
  if (currentUid) useStore.getState().setSyncStatus('offline');
};
const onOnline = (): void => {
  if (currentUid) useStore.getState().setSyncStatus('connecting');
};

function settle(): void {
  if (!currentUid) return;
  useStore.getState().setSyncStatus(navigator.onLine ? 'synced' : 'offline');
}

/** Begin syncing for a signed-in user. Idempotent per uid. */
export async function startSync(uid: string): Promise<void> {
  if (currentUid === uid) return;
  stopSync();
  currentUid = uid;
  setCurrentOwnerId(uid);

  // 1. Claim anonymous local docs for this account.
  await claimLocalDocuments(uid);

  // 2. If this account encrypts its synced notes and this device has no key,
  //    nothing starts: pushing would put plaintext into an encrypted vault, and
  //    pulling would replace readable notes with records nobody here can open.
  const vault = await vaultStatus(uid);
  useStore.getState().setVaultLocked(vault.state === 'locked');
  if (vault.state === 'locked') {
    useStore.getState().setSyncStatus('error');
    return;
  }

  useStore.getState().setSyncStatus(navigator.onLine ? 'connecting' : 'offline');
  window.addEventListener('offline', onOffline);
  window.addEventListener('online', onOnline);

  engine = createSyncEngine(
    dexieLocalSync,
    firestoreRemoteSync(uid, () => {
      // A vault created on another device while this one was running.
      useStore.getState().setVaultLocked(true);
      useStore.getState().setSyncStatus('error');
    }),
    {
      onConflict: (document) => {
        // Never resolve a divergence silently: the losing version is in history,
        // and the user is told where to find it.
        useStore.getState().setSyncConflict({ docId: document.id });
      },
      onRemoteContent: (document) => {
        // A change from another device: refresh it if it is open, and say so.
        useStore.getState().reloadOpenDoc?.(document.id);
        if (useStore.getState().activeDoc.id === document.id) {
          useStore.getState().setSyncNotice(t('sync.updated'));
        }
      },
      onPending: (count) => {
        if (!currentUid) return;
        if (count > 0) useStore.getState().setSyncStatus('connecting');
        else settle();
      },
      onError: () => useStore.getState().setSyncStatus('error'),
    },
  );

  // 2. Mirror local writes upward from now on.
  setLibraryWriteListener((id) => engine?.queueLocal(id));

  // 3. Pull what is there, then push what this device has that the cloud does not.
  await engine.start(uid);
  settle();
}

/** Stop syncing (on sign-out). Reverts to local-only. */
export function stopSync(): void {
  engine?.stop();
  engine = null;
  currentUid = null;
  setCurrentOwnerId(null);
  setLibraryWriteListener(null);
  window.removeEventListener('offline', onOffline);
  window.removeEventListener('online', onOnline);
  useStore.getState().setSyncStatus('off');
  useStore.getState().setVaultLocked(false);
  // The next account starts from a clean slate: agreements belong to one
  // library, and the key to one account — signing out is the gesture that says
  // this account is done on this device.
  void clearSyncState();
  void forgetVaultKey();
}

/**
 * Send every document up again.
 *
 * Used when what the cloud holds is no longer what it should be: encryption
 * was switched on (the copies there are plaintext), switched off, or a device
 * was unlocked and has to re-establish what both sides agree on. Clearing the
 * agreed state is what makes the next push send everything.
 */
export async function resyncEverything(): Promise<void> {
  const uid = currentUid;
  if (!uid) return;
  stopSync();
  await clearSyncState();
  await startSync(uid);
}
